import React, { useState, useEffect, useRef } from "react";
import {
  ChatBubbleLeftRightIcon,
  PaperAirplaneIcon,
  PaperClipIcon,
  FaceSmileIcon,
  XMarkIcon,
  MinusIcon,
  PhotoIcon,
  DocumentIcon,
  PhoneIcon,
  VideoCameraIcon,
  UserIcon,
  ClockIcon,
  CheckIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import {
  ChatBubbleLeftRightIcon as ChatBubbleLeftRightIconSolid,
  CheckIcon as CheckIconSolid,
} from "@heroicons/react/24/solid";
import Header from "../../components/Header";
import Footer from "../../components/Footer";

const LiveChat = () => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [agent, setAgent] = useState(null);
  const [chatStatus, setChatStatus] = useState("connecting"); // connecting, connected, waiting, ended
  const [isMinimized, setIsMinimized] = useState(false);
  const [waitTime, setWaitTime] = useState(0);
  const messagesEndRef = useRef(null);
  const chatInputRef = useRef(null);

  // Initialize chat
  useEffect(() => {
    // Mock agent data
    const supportAgents = [
      {
        id: 1,
        name: "Sarah Chen",
        avatar:
          "https://images.unsplash.com/photo-1494790108755-2616b612b510?w=40&h=40&fit=crop&crop=face",
        role: "IT Support Specialist",
        status: "online",
        rating: 4.9,
        responseTime: "Usually responds in 2-3 minutes",
      },
      {
        id: 2,
        name: "Marcus Johnson",
        avatar:
          "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=40&h=40&fit=crop&crop=face",
        role: "HR Support Specialist",
        status: "online",
        rating: 4.8,
        responseTime: "Usually responds in 1-2 minutes",
      },
      {
        id: 3,
        name: "Alex Rodriguez",
        avatar:
          "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=40&h=40&fit=crop&crop=face",
        role: "General Support",
        status: "online",
        rating: 4.7,
        responseTime: "Usually responds in 3-4 minutes",
      },
    ];

    // Simulate connection process
    const connectChat = async () => {
      setChatStatus("connecting");
      await new Promise((resolve) => setTimeout(resolve, 2000));

      // Assign random agent
      const randomAgent =
        supportAgents[Math.floor(Math.random() * supportAgents.length)];
      setAgent(randomAgent);
      setIsConnected(true);
      setChatStatus("connected");

      // Add welcome message
      const welcomeMessage = {
        id: Date.now(),
        type: "agent",
        content: `Hi! I'm ${randomAgent.name}, your ${randomAgent.role}. How can I help you today?`,
        timestamp: new Date(),
        status: "delivered",
      };
      setMessages([welcomeMessage]);
    };

    connectChat();
  }, []);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Simulate agent typing
  useEffect(() => {
    if (messages.length > 1 && messages[messages.length - 1].type === "user") {
      setIsTyping(true);
      const typingTimer = setTimeout(() => {
        setIsTyping(false);
        // Add agent response
        const responses = [
          "I understand your concern. Let me help you with that.",
          "Thank you for providing those details. I'm looking into this right now.",
          "I see the issue. Let me check our system and get back to you in just a moment.",
          "That's a great question! Let me pull up the relevant information for you.",
          "I've found the solution to your problem. Here's what you need to do:",
          "No problem at all! I'm happy to walk you through this step by step.",
        ];

        const randomResponse =
          responses[Math.floor(Math.random() * responses.length)];
        const agentResponse = {
          id: Date.now(),
          type: "agent",
          content: randomResponse,
          timestamp: new Date(),
          status: "delivered",
        };

        setMessages((prev) => [...prev, agentResponse]);
      }, 3000 + Math.random() * 2000);

      return () => clearTimeout(typingTimer);
    }
  }, [messages]);

  // Wait time counter
  useEffect(() => {
    if (chatStatus === "waiting") {
      const timer = setInterval(() => {
        setWaitTime((prev) => prev + 1);
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [chatStatus]);

  const sendMessage = () => {
    if (newMessage.trim() && isConnected) {
      const message = {
        id: Date.now(),
        type: "user",
        content: newMessage.trim(),
        timestamp: new Date(),
        status: "sent",
      };

      setMessages((prev) => [...prev, message]);
      setNewMessage("");

      // Mark as delivered after a short delay
      setTimeout(() => {
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === message.id ? { ...msg, status: "delivered" } : msg
          )
        );
      }, 1000);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatWaitTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  const quickResponses = [
    "I need help with password reset",
    "Having trouble accessing my payroll",
    "Question about leave policy",
    "IT equipment issue",
    "Training course enrollment",
    "Benefits information needed",
  ];

  const getMessageStatusIcon = (status) => {
    switch (status) {
      case "sent":
        return <CheckIcon className="w-3 h-3 text-gray-400" />;
      case "delivered":
        return <CheckIconSolid className="w-3 h-3 text-apollo-primary" />;
      case "read":
        return <CheckIconSolid className="w-3 h-3 text-green-500" />;
      default:
        return null;
    }
  };

  if (isMinimized) {
    return (
      <div className="fixed bottom-4 right-4 z-50">
        <button
          onClick={() => setIsMinimized(false)}
          className="bg-apollo-primary text-white rounded-full w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105"
        >
          <ChatBubbleLeftRightIconSolid className="w-5 h-5 sm:w-6 sm:h-6" />
          {messages.filter((m) => m.type === "agent" && !m.read).length > 0 && (
            <div className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 sm:w-5 sm:h-5 flex items-center justify-center text-xs">
              {messages.filter((m) => m.type === "agent" && !m.read).length}
            </div>
          )}
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-apollo-gray">
      <Header />

      <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <div className="max-w-3xl lg:max-w-4xl mx-auto">
          <div className="text-center mb-6 sm:mb-8">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 flex items-center justify-center">
              <ChatBubbleLeftRightIconSolid className="w-6 h-6 sm:w-8 sm:h-8 text-apollo-primary mr-2 sm:mr-3" />
              Live Chat Support
            </h1>
            <p className="mt-2 text-sm sm:text-base text-gray-600">
              Get instant help from our support team
            </p>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 shadow-lg overflow-hidden">
            {/* Chat Header */}
            <div className="bg-gradient-to-r from-apollo-primary to-apollo-accent text-white p-3 sm:p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2 sm:space-x-3">
                  {agent ? (
                    <>
                      <div className="relative">
                        <img
                          src={agent.avatar}
                          alt={agent.name}
                          className="w-8 h-8 sm:w-10 sm:h-10 rounded-full border-2 border-white"
                        />
                        <div className="absolute -bottom-1 -right-1 w-3 h-3 sm:w-4 sm:h-4 bg-green-500 rounded-full border-2 border-white"></div>
                      </div>
                      <div>
                        <h3 className="font-semibold text-sm sm:text-base">
                          {agent.name}
                        </h3>
                        <p className="text-xs sm:text-sm opacity-90">
                          {agent.role}
                        </p>
                      </div>
                    </>
                  ) : (
                    <div className="flex items-center space-x-2 sm:space-x-3">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 bg-white/20 rounded-full flex items-center justify-center">
                        <UserIcon className="w-4 h-4 sm:w-6 sm:h-6" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-sm sm:text-base">
                          Connecting...
                        </h3>
                        <p className="text-xs sm:text-sm opacity-90">
                          Finding available agent
                        </p>
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex items-center space-x-1 sm:space-x-2">
                  {chatStatus === "connected" && (
                    <>
                      <button className="p-1.5 sm:p-2 hover:bg-white/10 rounded-full transition-colors">
                        <PhoneIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                      </button>
                      <button className="p-1.5 sm:p-2 hover:bg-white/10 rounded-full transition-colors">
                        <VideoCameraIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                      </button>
                    </>
                  )}
                  <button
                    onClick={() => setIsMinimized(true)}
                    className="p-1.5 sm:p-2 hover:bg-white/10 rounded-full transition-colors"
                  >
                    <MinusIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                  </button>
                  <button className="p-1.5 sm:p-2 hover:bg-white/10 rounded-full transition-colors">
                    <XMarkIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                  </button>
                </div>
              </div>

              {/* Status Bar */}
              <div className="mt-2 sm:mt-3 flex flex-col sm:flex-row sm:items-center sm:justify-between text-xs sm:text-sm opacity-90 space-y-1 sm:space-y-0">
                <div className="flex items-center space-x-2 sm:space-x-4">
                  <div className="flex items-center space-x-1">
                    <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                    <span>
                      {chatStatus === "connected"
                        ? "Connected"
                        : "Connecting..."}
                    </span>
                  </div>
                  {agent && (
                    <span className="hidden sm:inline">
                      ⭐ {agent.rating} • {agent.responseTime}
                    </span>
                  )}
                </div>
                {chatStatus === "waiting" && (
                  <div className="flex items-center space-x-1">
                    <ClockIcon className="w-3 h-3 sm:w-4 sm:h-4" />
                    <span>Wait time: {formatWaitTime(waitTime)}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Chat Messages */}
            <div className="h-64 sm:h-80 lg:h-96 overflow-y-auto p-3 sm:p-4 bg-gray-50">
              {chatStatus === "connecting" && (
                <div className="flex flex-col items-center justify-center h-full">
                  <div className="animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-b-2 border-apollo-primary mb-4"></div>
                  <p className="text-gray-600 text-sm sm:text-base">
                    Connecting you to a support agent...
                  </p>
                  <p className="text-xs sm:text-sm text-gray-500 mt-1">
                    This usually takes 10-30 seconds
                  </p>
                </div>
              )}

              {messages.length === 0 && chatStatus === "connected" && (
                <div className="text-center text-gray-600 py-6 sm:py-8">
                  <ChatBubbleLeftRightIcon className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-4 text-gray-400" />
                  <p className="text-sm sm:text-base">
                    Start a conversation with your support agent
                  </p>
                </div>
              )}

              <div className="space-y-3 sm:space-y-4">
                {messages.map((message) => (
                  <div
                    key={message.id}
                    className={`flex ${
                      message.type === "user" ? "justify-end" : "justify-start"
                    }`}
                  >
                    <div
                      className={`max-w-xs sm:max-w-sm lg:max-w-md px-3 sm:px-4 py-2 sm:py-3 rounded-2xl ${
                        message.type === "user"
                          ? "bg-apollo-primary text-white"
                          : "bg-white border border-gray-200 text-gray-900"
                      }`}
                    >
                      <p className="text-sm leading-relaxed">
                        {message.content}
                      </p>
                      <div
                        className={`flex items-center justify-between mt-1 sm:mt-2 text-xs ${
                          message.type === "user"
                            ? "text-blue-100"
                            : "text-gray-500"
                        }`}
                      >
                        <span>{formatTime(message.timestamp)}</span>
                        {message.type === "user" && (
                          <div className="ml-2">
                            {getMessageStatusIcon(message.status)}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}

                {isTyping && (
                  <div className="flex justify-start">
                    <div className="bg-white border border-gray-200 rounded-2xl px-3 sm:px-4 py-2 sm:py-3 max-w-xs">
                      <div className="flex space-x-1">
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                        <div
                          className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                          style={{ animationDelay: "0.1s" }}
                        ></div>
                        <div
                          className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                          style={{ animationDelay: "0.2s" }}
                        ></div>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {agent?.name} is typing...
                      </p>
                    </div>
                  </div>
                )}
              </div>
              <div ref={messagesEndRef} />
            </div>

            {/* Quick Responses */}
            {messages.length <= 1 && chatStatus === "connected" && (
              <div className="border-t border-gray-200 p-3 sm:p-4 bg-white">
                <p className="text-xs sm:text-sm text-gray-600 mb-2 sm:mb-3">
                  Quick responses:
                </p>
                <div className="flex flex-wrap gap-2">
                  {quickResponses.map((response, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        setNewMessage(response);
                        chatInputRef.current?.focus();
                      }}
                      className="px-2 sm:px-3 py-1.5 sm:py-2 bg-gray-100 hover:bg-gray-200 rounded-full text-xs sm:text-sm text-gray-700 transition-colors"
                    >
                      {response}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Chat Input */}
            <div className="border-t border-gray-200 p-3 sm:p-4 bg-white">
              <div className="flex items-center space-x-2">
                <div className="flex-1 relative">
                  <input
                    ref={chatInputRef}
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder={
                      isConnected
                        ? "Type your message..."
                        : "Please wait while connecting..."
                    }
                    disabled={!isConnected}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 pr-10 sm:pr-12 border border-gray-300 rounded-full focus:ring-2 focus:ring-apollo-primary focus:border-apollo-primary disabled:bg-gray-100 disabled:cursor-not-allowed text-sm sm:text-base"
                  />
                  <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center space-x-1">
                    <button
                      className="p-1 sm:p-1.5 text-gray-400 hover:text-gray-600 transition-colors"
                      disabled={!isConnected}
                    >
                      <PaperClipIcon className="w-3 h-3 sm:w-4 sm:h-4" />
                    </button>
                    <button
                      className="p-1 sm:p-1.5 text-gray-400 hover:text-gray-600 transition-colors"
                      disabled={!isConnected}
                    >
                      <PhotoIcon className="w-3 h-3 sm:w-4 sm:h-4" />
                    </button>
                    <button
                      className="p-1 sm:p-1.5 text-gray-400 hover:text-gray-600 transition-colors"
                      disabled={!isConnected}
                    >
                      <FaceSmileIcon className="w-3 h-3 sm:w-4 sm:h-4" />
                    </button>
                  </div>
                </div>
                <button
                  onClick={sendMessage}
                  disabled={!newMessage.trim() || !isConnected}
                  className="p-2 sm:p-3 bg-apollo-primary text-white rounded-full hover:bg-apollo-secondary transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  <PaperAirplaneIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>
              </div>

              {/* File Upload Options */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mt-2 sm:mt-3 text-xs text-gray-500 space-y-1 sm:space-y-0">
                <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 space-y-1 sm:space-y-0">
                  <span className="flex items-center">
                    <ExclamationTriangleIcon className="w-3 h-3 mr-1" />
                    Max file size: 10MB
                  </span>
                  <span>Supported: Images, Documents, PDFs</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="hidden sm:inline">Press Enter to send</span>
                  <span className="hidden sm:inline">•</span>
                  <span className="hidden sm:inline">
                    Shift + Enter for new line
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Support Information */}
          <div className="mt-4 sm:mt-6 bg-white rounded-xl border border-gray-200 p-4 sm:p-6">
            <div className="text-center">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">
                Need different support?
              </h3>
              <p className="text-sm sm:text-base text-gray-600 mb-3 sm:mb-4">
                We offer multiple ways to get help
              </p>
              <div className="flex flex-col sm:flex-row sm:flex-wrap sm:justify-center gap-2 sm:gap-4">
                <a
                  href="tel:+1-800-275-5566"
                  className="flex items-center justify-center px-3 sm:px-4 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors text-sm sm:text-base"
                >
                  <PhoneIcon className="w-3 h-3 sm:w-4 sm:h-4 mr-2" />
                  Call Support
                </a>
                <a
                  href="mailto:support@apollo.health"
                  className="flex items-center justify-center px-3 sm:px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors text-sm sm:text-base"
                >
                  <DocumentIcon className="w-3 h-3 sm:w-4 sm:h-4 mr-2" />
                  Email Support
                </a>
                <a
                  href="/support/tickets/create"
                  className="flex items-center justify-center px-3 sm:px-4 py-2 bg-apollo-accent/10 text-apollo-accent rounded-lg hover:bg-apollo-accent/20 transition-colors text-sm sm:text-base"
                >
                  <DocumentIcon className="w-3 h-3 sm:w-4 sm:h-4 mr-2" />
                  Create Ticket
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default LiveChat;
